import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { siteInfo, comics, categories, testimonials } from '@/mocks/comics';

const faqs = [
  { q: '韩漫网上的漫画都是免费的吗？', a: '是的！韩漫网上的所有漫画资源均完全免费，无需付费即可畅享高清流畅的在线阅读体验。' },
  { q: '韩漫网的漫画更新速度快吗？', a: '我们每日与韩国同步更新最新章节，确保您能第一时间阅读到最新内容，热门作品通常在发布后数小时内即可在韩漫网阅读。' },
  { q: '韩漫网支持哪些设备阅读？', a: '韩漫网采用响应式设计，完美支持电脑、平板和手机等各类设备，您可以在任何设备上享受流畅的漫画阅读体验。' },
  { q: '如何在韩漫网找到想看的漫画？', a: '您可以通过首页的分类浏览功能按类型筛选漫画，也可以使用搜索功能直接查找您想看的作品。热门推荐和最新更新列表也能帮助您发现精彩内容。' },
  { q: '遇到无法加载的章节怎么办？', a: '请尝试刷新页面或清除浏览器缓存后重试。如问题持续存在，您可以通过页面下方的联系方式向我们反馈，我们会尽快修复。' },
  { q: '韩漫网有APP吗？', a: '目前韩漫网专注于网页版的优化体验。响应式设计已能为您提供接近原生APP的流畅阅读体验。APP版本正在开发中，敬请期待。' },
];

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    setFormStatus('idle');
    try {
      const response = await fetch('http://gxzzmm66.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries()) as [string, string]
        ).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络连接异常，请稍后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'http://gxzzmm66.cn/#organization',
    name: '韩漫网',
    url: 'http://gxzzmm66.cn/',
    logo: 'http://gxzzmm66.cn/logo.png',
    description: siteInfo.description,
    foundingDate: siteInfo.establishedYear,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: '客户服务',
      email: 'hello@gxzzmm66.cn',
      availableLanguage: ['Chinese'],
    },
    sameAs: ['http://gxzzmm66.cn/'],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': 'http://gxzzmm66.cn/about',
    url: 'http://gxzzmm66.cn/about',
    name: '关于韩漫网',
    description: siteInfo.description,
    inLanguage: 'zh-CN',
    isPartOf: { '@id': 'http://gxzzmm66.cn/#website' },
    about: { '@id': 'http://gxzzmm66.cn/#organization' },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <>
      <SEO
        title="关于我们"
        description="韩漫网是国内领先的免费韩漫在线阅读平台，致力于为广大漫画爱好者提供最新、最全、最高清的韩国漫画资源"
        ogType="article"
        canonicalUrl="http://gxzzmm66.cn/about"
        schemaData={{ ...orgSchema, ...webpageSchema, ...faqSchema }}
      />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        {/* Hero */}
        <section className="relative h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20inviting%20reading%20environment%20with%20soft%20ambient%20lighting%20cozy%20bookshelves%20and%20comfortable%20seating%20cream%20and%20warm%20amber%20color%20palette%20Korean%20aesthetic%20peaceful%20atmosphere%20clean%20modern%20interior%20editorial%20style&width=1600&height=500&seq=about-hero&orientation=landscape"
            alt="关于韩漫网"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-white mb-3">关于韩漫网</h1>
              <p className="text-white/80 text-sm md:text-base max-w-md mx-auto">致力为每一位漫迷打造最好的免费韩漫阅读体验</p>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-16 md:py-24 space-y-20 md:space-y-28">
          {/* Brand Story */}
          <section>
            <div className="flex flex-col md:flex-row gap-12">
              <div className="md:w-1/2">
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium mb-4">品牌故事</span>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4">让好漫画，触达每一个人</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  韩漫网创立于{siteInfo.establishedYear}年，初衷很简单：我们希望每一位喜欢韩国漫画的读者，都能在最简单、最纯粹的环境中，免费阅读到最新、最好的韩漫作品。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  从最初的几百部作品，发展到如今超过一万五千部的庞大漫画库，韩漫网已经成长为国内最具影响力的免费韩漫阅读平台之一。我们的团队始终坚持每日同步韩国最新更新，确保读者零延迟享受精彩内容。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们不仅是一个漫画网站，更是一个连接漫迷与优质内容的桥梁。每一部漫画的收录、每一个章节的更新，都承载着我们对漫画文化的热爱与尊重。
                </p>
              </div>
              <div className="md:w-1/2">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20team%20working%20together%20in%20bright%20cozy%20office%20space%20with%20large%20screens%20displaying%20colorful%20comic%20artwork%20warm%20natural%20light%20collaborative%20atmosphere%20minimal%20clean%20design%20Korean%20aesthetic&width=800&height=600&seq=about-story&orientation=landscape"
                    alt="韩漫网团队"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="bg-background-100 rounded-3xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">平台数据</h2>
              <p className="text-sm text-foreground-500">用数据见证我们的成长</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-5 bg-background-50 rounded-2xl">
                <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">{siteInfo.totalComics}</div>
                <div className="text-xs text-foreground-500 mt-2">收录漫画</div>
              </div>
              <div className="text-center p-5 bg-background-50 rounded-2xl">
                <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">{siteInfo.totalUsers}</div>
                <div className="text-xs text-foreground-500 mt-2">注册用户</div>
              </div>
              <div className="text-center p-5 bg-background-50 rounded-2xl">
                <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">{siteInfo.dailyVisits}</div>
                <div className="text-xs text-foreground-500 mt-2">日访问量</div>
              </div>
              <div className="text-center p-5 bg-background-50 rounded-2xl">
                <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">{categories.length}+</div>
                <div className="text-xs text-foreground-500 mt-2">漫画分类</div>
              </div>
            </div>
          </section>

          {/* Contact Info */}
          <section id="contact">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-medium mb-4">联系方式</span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">商务合作与联系我们</h2>
              <p className="text-sm text-foreground-500 max-w-md mx-auto">无论是商务合作、版权沟通还是意见建议，我们随时恭候您的来信</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Contact Cards */}
              <div className="space-y-4">
                <div className="p-5 bg-background-100 rounded-2xl flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">商务合作邮箱</p>
                    <p className="text-xs text-foreground-500 mt-0.5">hello@gxzzmm66.cn</p>
                  </div>
                </div>
                <div className="p-5 bg-background-100 rounded-2xl flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100">
                    <i className="ri-customer-service-2-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">客服咨询</p>
                    <p className="text-xs text-foreground-500 mt-0.5">hello@gxzzmm66.cn</p>
                  </div>
                </div>
                <div className="p-5 bg-background-100 rounded-2xl flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100">
                    <i className="ri-map-pin-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">公司地址</p>
                    <p className="text-xs text-foreground-500 mt-0.5">上海市浦东新区张江高科技园区</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                <h3 className="text-base font-semibold text-foreground-900 mb-5">商务合作表单</h3>
                <form id="contact-form" onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="spam-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">您的姓名</label>
                      <input type="text" name="name" required className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="请输入姓名" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">联系邮箱</label>
                      <input type="email" name="email" required className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="请输入邮箱" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">合作类型</label>
                    <select name="cooperation_type" required className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer">
                      <option value="">请选择合作类型</option>
                      <option value="ad">广告投放</option>
                      <option value="copyright">版权合作</option>
                      <option value="translation">翻译合作</option>
                      <option value="tech">技术合作</option>
                      <option value="other">其他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea name="message" required maxLength={500} rows={4} className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none" placeholder="请简要描述您的合作需求或建议"></textarea>
                    <p className="text-[10px] text-foreground-400 mt-1">最多500字符</p>
                  </div>
                  <button type="submit" className="w-full py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    提交合作申请
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-primary-600 text-center">提交成功！我们会尽快与您联系。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-600 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium mb-4">常见问题</span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">您可能想知道的</h2>
              <p className="text-sm text-foreground-500">快速解答您的疑问</p>
            </div>
            <div className="max-w-2xl mx-auto space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-background-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section>
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">用户口碑</h2>
              <p className="text-sm text-foreground-500">超过580万用户的选择</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.slice(0, 3).map((t) => (
                <div key={t.id} className="bg-background-100 rounded-2xl p-6">
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <div key={s} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${s < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}